/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import get from 'lodash/get';
import cx from 'classnames';
import { Col, Row } from 'react-bootstrap';
import mixpanel from '../../../../src/web/utils/mixpanel';
import CurrentPageContext from '../../../contexts/CurrentPageContext';
import EditCouponModal from '../../../../src/web/components/EditCouponModal';
import CouponModal from '../../../components/stateful/CouponModal';
import Shield from '../../../components/stateless/Shield';
import GiftTag from '../../../components/stateless/GiftTag';
import Hexagon from '../../../components/stateless/Hexagon';
import Starburst from '../../../components/stateless/Starburst';
import SpecialOffer from '../../../components/stateless/SpecialOffer';
import styles from './styles.scss';

const propTypes = {
  color: PropTypes.string,
  site: PropTypes.shape({
    companyId: PropTypes.string.isRequired,
    identifier: PropTypes.string.isRequired,
  }).isRequired,
  facilityId: PropTypes.string.isRequired,
  handleRequest: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  isEditable: PropTypes.bool,
  isEditing: PropTypes.bool,
  isRequesting: PropTypes.bool,
  onCancelEdit: PropTypes.func,
  onFinishEdit: PropTypes.func,
  requested: PropTypes.bool,
  errorMessage: PropTypes.string,
  show: PropTypes.bool,
  showModal: PropTypes.func.isRequired,
  style: PropTypes.string,
  text: PropTypes.string,
  title: PropTypes.string,
  facility: PropTypes.shape({
    currencySymbol: PropTypes.string.isRequired,
  }),
};

const defaultProps = {
  color: undefined,
  facility: undefined,
  isEditing: false,
  isEditable: false,
  errorMessage: '',
  isRequesting: undefined,
  onCancelEdit: undefined,
  onFinishEdit: undefined,
  requested: undefined,
  show: undefined,
  style: undefined,
  text: undefined,
  title: undefined,
};

// eslint-disable-next-line react/prop-types
const renderCouponStyle = ({ style, color, large, currencySymbol, primaryColor }) => {
  switch (style) {
    case 'gift-tag':
      return (
        <GiftTag
          color={color || primaryColor}
          width={large ? '445px' : '370px'}
          height={large ? '216px' : '179px'}
        />
      );
    case 'shield':
      return (
        <Shield
          color={color || primaryColor}
          width={large ? '445px' : '370px'}
          height={large ? '217px' : '163px'}
        />
      );
    case 'starburst':
      return (
        <Starburst
          color={color || primaryColor}
          width={large ? '445px' : '370px'}
          height={large ? '230px' : '181px'}
        />
      );
    case 'hexagon':
      return (
        <Hexagon
          color={color || primaryColor}
          width={large ? '445px' : '370px'}
          height={large ? '227px' : '178px'}
        />
      );
    default:
      return (
        <SpecialOffer
          color={color || primaryColor}
          width={large ? '445px' : '370px'}
          height={large ? '212px' : '179px'}
          currencySymbol={currencySymbol}
        />
      );
  }
};

class Coupon extends React.Component {
  static contextType = CurrentPageContext;

  handleSubmitSuccess = (response) => {
    const { site: { companyId, identifier } } = this.props;

    mixpanel.trackCoupon(
      { companyId, companyIdentifier: identifier },
      get(response, 'data.tenant'),
    );
  };

  openModal = () => {
    const { showModal } = this.props;
    showModal();
  }

  renderText(color) {
    const {
      style, text, title,
    } = this.props;

    const { primaryColor } = this.context;

    if (style === 'special-offer' || style === 'gift-tag') {
      return (
        <div>
          <div
            className={cx(styles.title, styles.titleOne)}
            style={{ color: color || primaryColor }}
          >
            <div dangerouslySetInnerHTML={{ __html: title }} />
          </div>
          <div className={cx(styles.text, styles.textOne)}>
            <div dangerouslySetInnerHTML={{ __html: text }} />
          </div>
        </div>
      );
    }

    return (
      <Row>
        <Col xs={6} className={`${styles.title} ${styles.titleTwo}`}>
          <div dangerouslySetInnerHTML={{ __html: title }} />
        </Col>
        <Col xs={6} className={`${styles.text} ${styles.textTwo}`}>
          <div dangerouslySetInnerHTML={{ __html: text }} />
        </Col>
      </Row>
    );
  }

  renderCouponModal() {
    const {
      color,
      style,
      text,
      title,
      facilityId,
      handleRequest,
      hideModal,
      isRequesting,
      requested,
      errorMessage,
      show,
      site: { publicLogo: { url, mediumId } },
      facility = { currencySymbol: '$' },
    } = this.props;
    const coupon = {
      color, style, text, title,
    };

    const { currencySymbol } = facility;

    return (
      <CouponModal
        coupon={coupon}
        headerLogoUrl={url}
        headerLogoMediumId={mediumId}
        facilityId={facilityId}
        isRequesting={isRequesting}
        onClose={hideModal}
        onSubmit={handleRequest}
        onSubmitSuccess={this.handleSubmitSuccess}
        requested={requested}
        errorMessage={errorMessage}
        showCoupon={show}
        currencySymbol={currencySymbol}
      />
    );
  }

  renderEditCouponModal() {
    const {
      color,
      style,
      text,
      title,
      hideModal,
      onCancelEdit,
      onFinishEdit,
      isEditing,
      facilityId,
      facility = { currencySymbol: '$' },
    } = this.props;

    const { currencySymbol } = facility;

    const { primaryColor, secondaryColor, tertiaryColor } = this.context;

    const themeColors = {
      primary: primaryColor,
      secondary: secondaryColor,
      tertiary: tertiaryColor,
    };

    return (
      <EditCouponModal
        color={color}
        colors={themeColors}
        style={style}
        text={text}
        title={title}
        onCancelEdit={onCancelEdit}
        onClose={hideModal}
        renderCouponStyle={renderCouponStyle}
        onFinishEdit={onFinishEdit}
        showEditCoupon={isEditing}
        facilityId={facilityId}
        currencySymbol={currencySymbol}
      />
    );
  }

  render() {
    const {
      isEditable, isEditing, style, facility = { currencySymbol: '$' },
    } = this.props;
    const { color } = this.props;
    const { currencySymbol } = facility;

    const { primaryColor, secondaryColor } = this.context;
    const couponColor = (() => {
      switch (color) {
        case 'primary':
          return primaryColor;
        case 'secondary':
          return secondaryColor;
        default:
          return color || secondaryColor;
      }
    })();

    return (
      <div>
        {/* eslint-disable jsx-a11y/click-events-have-key-events */}
        {/* eslint-disable jsx-a11y/no-static-element-interactions */}
        <div className={styles.coupon} onClick={this.openModal}>
          {/* eslint-enable jsx-a11y/click-events-have-key-events */}
          {/* eslint-enable jsx-a11y/no-static-element-interactions */}
          <div>
            <Col className="d-none d-sm-block">
              {renderCouponStyle({
                style, color: couponColor, large: true, currencySymbol, primaryColor,
              })}
            </Col>
            <Col className="d-block d-sm-none">
              {renderCouponStyle({
                style, color: couponColor, large: false, currencySymbol, primaryColor,
              })}
            </Col>
          </div>
          {this.renderText(couponColor)}
        </div>
        {isEditing ? this.renderEditCouponModal() : null}
        {isEditable ? null : this.renderCouponModal()}
      </div>
    );
  }
}

Coupon.propTypes = propTypes;
Coupon.defaultProps = defaultProps;

Coupon.fragments = {
  facility: gql`
    fragment CouponFacility on Facility {
      currencySymbol
    }
  `,
  site: gql`
    fragment CouponSite on Site {
      companyId
      identifier
      publicLogo {
        mediumId
        url
      }
    }
  `,
};

export default Coupon;
